import { format } from 'date-fns';
import { FC } from 'react';

import { dateFormatInverted } from 'constants/formats';
import { useTranslate } from 'hooks/useTranslate';
import { BeneficiaryWithEnvelopeId } from 'modules/beneficiaries/hooks/useGetBeneficiariesPendingEnvelopes';

type BeneficiariesPendingSignatureToolbarProps = {
  showHeaders: {
    DOWNLOAD: boolean;
    SEND_REMINDER: boolean;
    RESEND_INVITATION: boolean;
    MIN_DATE: boolean;
  };
  beneficiaries: BeneficiaryWithEnvelopeId[];
  filteredCount: number;
  isFiltered: boolean;
  isResendingInvitations: boolean;
  minDate: Date | null;
  eligibleBeneficiariesInCurrentPage: number; // Count of eligible beneficiaries in current page
  onDownloadList: () => void;
  onSendReminder: () => void;
  onResendInvitation: () => void;
  onMinDateChange: (date: Date | null) => void; // eslint-disable-line no-unused-vars
};

const BeneficiariesPendingSignatureToolbar: FC<
  BeneficiariesPendingSignatureToolbarProps
> = ({
  showHeaders,
  beneficiaries,
  filteredCount,
  isFiltered,
  isResendingInvitations,
  minDate,
  eligibleBeneficiariesInCurrentPage,
  onDownloadList,
  onSendReminder,
  onResendInvitation,
  onMinDateChange,
}) => {
  const { t } = useTranslate();

  return (
    <div className="nk-block-head-content mb-3">
      <ul className="nk-block-tools justify-content-md-end g-3 flex-wrap align-items-end">
        {showHeaders.DOWNLOAD && (
          <li className="order-md-last">
            <button
              type="button"
              className="btn btn-white btn-outline-light ml-5"
              onClick={onDownloadList}
              disabled={isResendingInvitations}
            >
              <em className="icon ni ni-download-cloud" />
              <span>{t('DownloadListing')}</span>
            </button>
          </li>
        )}

        {showHeaders.SEND_REMINDER && (
          <li className="order-md-last">
            <button
              type="button"
              className="btn btn-dim btn-primary"
              disabled={beneficiaries.length === 0 || isResendingInvitations}
              onClick={onSendReminder}
            >
              <em className="icon ni ni-send" />
              <span>{t('SendReminder')}</span>
            </button>
          </li>
        )}

        {showHeaders.RESEND_INVITATION && (
          <li className="order-md-last">
            <button
              type="button"
              className={`btn ${
                isFiltered ? 'btn-warning' : 'btn-primary'
              } btn-dim`}
              disabled={
                filteredCount === 0 ||
                eligibleBeneficiariesInCurrentPage === 0 ||
                isResendingInvitations
              }
              onClick={onResendInvitation}
              title={
                isFiltered
                  ? t('resendToFilteredBeneficiaries')
                  : t('resendToAllBeneficiaries')
              }
            >
              <em className="icon ni ni-redo" />
              <span>
                {isFiltered
                  ? `${t(
                      'ResendInvitation'
                    )} (${eligibleBeneficiariesInCurrentPage})`
                  : `${t(
                      'ResendInvitation'
                    )} (${eligibleBeneficiariesInCurrentPage})`}
              </span>
            </button>
          </li>
        )}

        {showHeaders.MIN_DATE && (
          <li className="order-md-last">
            <div className="form-group">
              <label className="form-label" htmlFor="filter-end-date">
                <em className="icon ni ni-filter mr-1" />
                {t('FilterByPlanStartDate')}
              </label>
              <div className="form-control-wrap">
                <input
                  type="date"
                  className="form-control"
                  id="filter-end-date"
                  value={minDate ? format(minDate, dateFormatInverted) : ''}
                  onChange={(e) => onMinDateChange(new Date(e.target.value))}
                  placeholder={t('selectDateToFilter')}
                  disabled={isResendingInvitations}
                />
              </div>
            </div>
          </li>
        )}
      </ul>
    </div>
  );
};

export default BeneficiariesPendingSignatureToolbar;
